/**
 * Subscription tier enum matching backend SubscriptionTier
 */
export enum SubscriptionTier {
  Free = 0,
  Launch = 5,
  Basic = 10,
  Plus = 20,
  Premium = 30,
  Enterprise = 40,
}

/**
 * Subscription status enum matching backend SubscriptionStatus
 */
export enum SubscriptionStatus {
  Trial = 0,
  Active = 10,
  PastDue = 20,
  Cancelled = 30,
  Expired = 40,
  Suspended = 50,
}

/**
 * Maps a string tier value from the backend to the SubscriptionTier enum
 */
export function parseSubscriptionTier(tier: string | number | undefined): SubscriptionTier {
  if (typeof tier === 'number') return tier as SubscriptionTier;
  if (!tier) return SubscriptionTier.Free;

  const tierLower = tier.toLowerCase();

  switch (tierLower) {
    case 'free':
      return SubscriptionTier.Free;
    case 'basic':
    case 'launch':
      return SubscriptionTier.Launch;
    case 'plus':
    case 'grow':
      return SubscriptionTier.Plus;
    case 'premium':
    case 'scale':
      return SubscriptionTier.Premium;
    case 'enterprise':
      return SubscriptionTier.Enterprise;
    case 'trial':
      // Trial users get Free tier features until they subscribe
      return SubscriptionTier.Free;
    default:
      console.warn(`Unknown subscription tier: ${tier}`);

      return SubscriptionTier.Free;
  }
}

/**
 * Maps a string status value from the backend to the SubscriptionStatus enum
 */
export function parseSubscriptionStatus(status: string | number | undefined): SubscriptionStatus {
  if (typeof status === 'number') return status as SubscriptionStatus;
  if (!status) return SubscriptionStatus.Trial;

  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case 'trial':
    case 'trialing':
      return SubscriptionStatus.Trial;
    case 'active':
      return SubscriptionStatus.Active;
    case 'past_due':
    case 'pastdue':
      return SubscriptionStatus.PastDue;
    case 'cancelled':
    case 'canceled':
      return SubscriptionStatus.Cancelled;
    case 'expired':
    case 'none':
      return SubscriptionStatus.Expired;
    case 'suspended':
    case 'paused':
      return SubscriptionStatus.Suspended;
    default:
      console.warn(`Unknown subscription status: ${status}`);

      return SubscriptionStatus.Expired;
  }
}

/**
 * Feature flags for subscriptions
 */
export interface SubscriptionFeatures {
  videoConferencing: boolean;
  advancedAnalytics: boolean;
  customIntegrations: boolean;
  prioritySupport: boolean;
  whiteLabeling: boolean;
}

/**
 * DTO matching the backend SubscriptionDto
 */
export interface SubscriptionDto {
  practiceId: string;
  practiceAdminEmail: string;
  practiceName: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  startDate: string;
  currentPeriodEnd: string;
  trialEndDate?: string;
  paymentProviderId?: string;
  maxPractitioners: number;
  maxPatients: number;
  features: SubscriptionFeatures;
}

/**
 * Subscription tier details with pricing and features
 */
export interface SubscriptionTierDetails {
  tier: SubscriptionTier;
  name: string;
  price: number;
  description: string;
  features: string[];
  maxPractitioners: number;
  maxPatients: number;
}

/**
 * Pricing and feature details for each subscription tier
 */
export const subscriptionTierDetails: SubscriptionTierDetails[] = [
  {
    tier: SubscriptionTier.Free,
    name: 'Free',
    price: 0,
    description: 'Basic functionality for solo practitioners',
    features: ['1 practitioner', '10 patients', 'Basic client records', 'Limited scheduling'],
    maxPractitioners: 1,
    maxPatients: 10,
  },
  {
    tier: SubscriptionTier.Launch,
    name: 'Launch',
    price: 0,
    description: 'Full-featured trial for new practices',
    features: [
      '1 practitioner',
      '10 clients',
      'HIPAA-compliant messaging',
      'Basic scheduling',
      'Client portal',
      'Health metrics tracking',
    ],
    maxPractitioners: 1,
    maxPatients: 10,
  },
  {
    tier: SubscriptionTier.Basic,
    name: 'Basic',
    price: 29,
    description: 'Essential tools for small practices',
    features: [
      'Up to 3 practitioners',
      'Up to 100 patients',
      'Video conferencing',
      'Client portal',
      'Basic scheduling',
      'HIPAA-compliant messaging',
    ],
    maxPractitioners: 3,
    maxPatients: 100,
  },
  {
    tier: SubscriptionTier.Plus,
    name: 'Plus',
    price: 99,
    description: 'Enhanced tools for growing practices',
    features: [
      'Up to 10 practitioners',
      'Up to 500 patients',
      'Advanced analytics',
      'Custom forms',
      'Document management',
      'Group scheduling',
      'Team management',
    ],
    maxPractitioners: 10,
    maxPatients: 500,
  },
  {
    tier: SubscriptionTier.Premium,
    name: 'Premium',
    price: 249,
    description: 'Comprehensive solution for established practices',
    features: [
      'Up to 25 practitioners',
      'Up to 2,000 patients',
      'Custom integrations',
      'Priority support',
      'Advanced reporting',
      'Workflow automation',
      'Multi-location support',
    ],
    maxPractitioners: 25,
    maxPatients: 2000,
  },
  {
    tier: SubscriptionTier.Enterprise,
    name: 'Enterprise',
    price: 499,
    description: 'Tailored solutions for large organizations',
    features: [
      'Up to 100 practitioners',
      'Up to 10,000 patients',
      'White labeling',
      'Dedicated support',
      'Custom development',
      'SLA guarantees',
      'Enterprise integrations',
      'Multi-entity management',
    ],
    maxPractitioners: 100,
    maxPatients: 10000,
  },
];
